<template>
    <el-dialog
        class="app-lk-user-call-dialog"
        :visible.sync="isVisible"
        :width="modalWidth"
        :close-on-click-modal="false"
        @close="_onDialogClose"
    >
        <div
            v-loading="loading"
            class="app-lk-user-call-dialog__content"
        >
            <i class="el-icon-phone-outline app-lk-user-call-dialog__icon" />
            <div class="app-lk-user-call-dialog__phone">
                {{ maskedPhoneText }}
            </div>
            <div
                v-if="currentDescriptionText"
                class="app-lk-user-call-dialog__text"
            >
                {{ currentDescriptionText }}
            </div>
            <div class="app-lk-user-call-dialog__call-action">
                <a
                    v-if="currentPhoneText && isCallAvailable"
                    :href="phoneHref"
                    class="app-lk-user-call-dialog__call-link"
                >
                    <el-button
                        type="primary"
                        class="app-lk-user-call-dialog__call-button"
                    >
                        Позвонить
                    </el-button>
                </a>
                <span
                    v-else-if="currentPhoneText"
                    class="app-lk-user-call-dialog__call-link"
                >
                    <el-button
                        type="primary"
                        class="app-lk-user-call-dialog__call-button"
                        disabled
                    >
                        Позвонить
                    </el-button>
                </span>
            </div>
            <div
                v-if="showActions && !showTimerText"
                class="app-lk-user-call-dialog__controls"
            >
                <el-button
                    type="primary"
                    plain
                    @click="_onRepeatClick"
                >
                    Получить новый номер
                </el-button>
                <el-button
                    v-if="showFallbackAction"
                    type="info"
                    @click="_onFallbackClick"
                >
                    Войти другим способом
                </el-button>
            </div>
            <div
                v-if="showExpiryTimer"
                class="app-lk-user-call-dialog__timer-bar"
            >
                Сделайте звонок в течение: {{ formattedExpiryTime }} мин.
            </div>
        </div>
    </el-dialog>
</template>

<script>
export default {
    name: 'ConfirmUserCallModal',
    props: {
        loading: {
            type: Boolean,
            default: false
        },
        descriptionText: {
            type: String,
            default: 'Если звонка нет, позвоните на номер для входа'
        },
        callPhone: {
            type: String,
            default: ''
        },
        cooldownSecondsLeft: {
            type: Number,
            default: 0
        },
        expiresAt: {
            type: String,
            default: ''
        },
        timeOut: {
            type: Number,
            default: 0
        },
        showActions: {
            type: Boolean,
            default: false
        },
        showFallbackAction: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isVisible: false,
            expirySecondsLeft: 0,
            expiryTimer: null
        };
    },
    computed: {
        showCooldownText() {
            return this.cooldownSecondsLeft > 0;
        },
        showTimerText() {
            return this.timeOut > 0;
        },
        currentDescriptionText() {
            return this.descriptionText
                || 'Сделайте бесплатный исходящий звонок с вашего телефона на этот номер, чтобы пройти идентификацию.';
        },
        currentPhoneText() {
            return this.showCooldownText
                ? ''
                : this.callPhone;
        },
        phoneHref() {
            const normalizedPhone = (this.currentPhoneText || '').replace(/[^\d+]/g, '');

            return normalizedPhone ? `tel:${normalizedPhone}` : '';
        },
        maskedPhoneText() {
            const digits = (this.currentPhoneText || '').replace(/\D/g, '');

            if (digits.length === 11 && /^[78]/.test(digits)) {
                return `8-${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
            }

            if (digits.length === 11) {
                return `+${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
            }

            if (digits.length === 10) {
                return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
            }

            return this.currentPhoneText;
        },
        showExpiryTimer() {
            return this.expirySecondsLeft > 0;
        },
        isCallAvailable() {
            return this.expirySecondsLeft > 0;
        },
        formattedExpiryTime() {
            const minutes = Math.floor(this.expirySecondsLeft / 60);
            const seconds = this.expirySecondsLeft % 60;

            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        },
        modalWidth() {
            return window.innerWidth > 680 ? '450px' : '100%';
        }
    },
    watch: {
        expiresAt: {
            immediate: true,
            handler() {
                this._startExpiryTimer();
            }
        }
    },
    beforeDestroy() {
        this._stopExpiryTimer();
    },
    methods: {
        openModal() {
            this.isVisible = true;
        },

        closeModal() {
            this.isVisible = false;
        },

        _onRepeatClick() {
            this.$emit('repeat-call');
        },

        _onFallbackClick() {
            this.$emit('fallback-click');
        },

        _onDialogClose() {
            this.$emit('close-modal');
        },

        _startExpiryTimer() {
            this._stopExpiryTimer();
            this._updateExpirySecondsLeft();

            if (!this.expirySecondsLeft) {
                return;
            }

            this.expiryTimer = setInterval(() => {
                this._updateExpirySecondsLeft();

                if (!this.expirySecondsLeft) {
                    this._stopExpiryTimer();
                }
            }, 1000);
        },

        _updateExpirySecondsLeft() {
            const expiresAt = new Date(this.expiresAt).getTime();

            this.expirySecondsLeft = Number.isNaN(expiresAt)
                ? 0
                : Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
        },

        _stopExpiryTimer() {
            clearInterval(this.expiryTimer);
            this.expiryTimer = null;
        }
    }
};
</script>

<style lang="scss">
.app-lk-user-call-dialog {
  .el-dialog {
    min-width: 330px;
    max-width: 450px;
    border-radius: 8px;
    overflow: hidden;
  }
  .el-dialog__body {
    padding-bottom: 20px;
  }
  &__content {
    text-align: center;
  }
  &__header {
    font-size: 36px;
    font-family: "PT Sans", sans-serif;
    color: #000000;
    margin-bottom: 20px;
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }
  &__icon {
    display: block;
    color: #409EFF;
    font-size: 88px;
    margin-bottom: 18px;
  }
  &__text {
    max-width: 390px;
    margin: 0 auto 26px;
    font-size: 18px;
    line-height: 1.4;
    word-break: break-word;
  }
  &__phone {
    min-height: 38px;
    margin-bottom: 24px;
    font-size: 32px;
    line-height: 1.2;
    word-break: break-word;
  }
  &__call-action {
    margin-bottom: 24px;
  }
  &__call-link {
    display: block;
    text-decoration: none;
  }
  &__call-button {
    width: 100%;
    max-width: 250px;
    min-height: 54px;
    font-size: 20px;
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
  }
  &__controls {
    display: flex;
    justify-content: center;
    gap: 12px;
    align-items: center;
    margin: 20px 0;

    .el-button {
      flex: 0 1 200px;
      width: 100%;
      max-width: 200px;
      min-width: 210px;
      margin: 0;
      white-space: normal;
      word-break: normal;
      overflow-wrap: normal;
    }

    .el-button + .el-button {
      margin-left: 0;
    }

    @media (max-width: 680px) {
      flex-direction: column;

      .el-button {
        flex: none;
      }
    }
  }
  &__timer-bar {
    margin: 28px -20px -20px;
    padding: 10px 16px;
    color: #ffffff;
        background: #67C23A;
    font-size: 16px;
    font-family: "PT Sans", sans-serif;
  }

  @media (max-width: 680px) {
    .el-dialog {
      width: calc(100% - 24px) !important;
      margin-top: 4vh !important;
    }
    .el-dialog__body {
      padding: 20px 16px;
    }
    &__header {
      font-size: 30px;
    }
    &__phone {
      font-size: 28px;
    }
    &__timer-bar {
      margin-right: -16px;
      margin-left: -16px;
    }
  }

  @media (max-width: 353px) {
    .el-dialog {
      min-width: calc(100vw - 24px);
    }
  }
}
</style>
